// Browser-Use Agent client service.
// Talks to MyAMP (/api/ams/browser-agent/*). Streams a run over SSE, mirroring the
// ReadableStream/`data:` parsing used by momentumClawService.sendClawMessage.

const BASE_URL = process.env.REACT_APP_MyAMP_Url;
const RUN_URL = `${BASE_URL}/api/ams/browser-agent/run`;
const RUNS_URL = `${BASE_URL}/api/ams/browser-agent/runs`;

export interface AgentStep {
  step_num: number;
  action: string;
  target?: string | null;
  input_value?: string | null;
  reasoning?: string | null;
  ok: boolean;
  result_text?: string | null;
  has_screenshot?: boolean;  // a per-step screenshot is stored (history filmstrip)
}

export interface RunSummary {
  run_id: string;
  task: string;
  starting_url?: string | null;
  status: string;
  step_count: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface RunDetail extends RunSummary {
  result?: Record<string, any> | null;
  error_message?: string | null;
  steps: AgentStep[];
}

export interface InputRequest {
  stepNum: number;
  prompt: string;
  inputKind: 'text' | 'credentials';
  domain: string;
}

export type FieldType = 'string' | 'number' | 'date' | 'boolean';
export interface FieldDef {
  name: string;
  type: FieldType;
  required: boolean;
  description?: string;
}

export interface SchemaError { index: number; field: string; msg: string; }

export interface ExtractConfig {
  schema: FieldDef[];
  output?: 'table' | 'json';
  maxPages?: number;
}

export interface RunCallbacks {
  onRunId?: (runId: string) => void;
  onStep: (step: AgentStep) => void;
  onScreenshot: (stepNum: number, imageB64: string, url: string) => void;
  onResult: (status: string, output: string, data?: any, schemaErrors?: SchemaError[]) => void;
  onError: (message: string) => void;
  onDone: (status: string) => void;
  onInputRequest?: (req: InputRequest) => void;
  onExtractProgress?: (count: number, page: number) => void;
}

export interface LoginExtraField {
  label: string;
  value: string;
}

export type InputPayload =
  | { kind: 'text'; text: string }
  | { kind: 'credentials'; username: string; password: string; extraFields?: LoginExtraField[] };

/** Answer an ask_user pause for a running agent. */
export async function sendInput(runId: string, payload: InputPayload): Promise<boolean> {
  try {
    // Translate camelCase extraFields -> snake_case extra_fields for the API.
    const body: Record<string, any> =
      payload.kind === 'credentials'
        ? {
            kind: 'credentials',
            username: payload.username,
            password: payload.password,
            extra_fields: payload.extraFields?.length ? payload.extraFields : undefined,
          }
        : payload;
    const res = await fetch(`${RUNS_URL}/${encodeURIComponent(runId)}/input`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch {
    return false;
  }
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('authToken') ?? '';
  return { Authorization: `Bearer ${token}` };
}

/** Final-screenshot URL for a completed run (PNG). */
export function runScreenshotUrl(runId: string): string {
  return `${RUNS_URL}/${encodeURIComponent(runId)}/screenshot`;
}

/** Per-step screenshot URL (JPEG) for a run's history filmstrip. */
export function stepScreenshotUrl(runId: string, stepNum: number): string {
  return `${RUNS_URL}/${encodeURIComponent(runId)}/steps/${stepNum}/screenshot`;
}

/** Open a PERSISTENT browser session on a URL; the run can then continue in it.
 *  If username/password are given, the server auto-logs-in before screenshotting. */
export async function openSession(
  url: string,
  username?: string,
  password?: string,
  extraFields?: LoginExtraField[],
  signal?: AbortSignal
): Promise<{ sessionId?: string; url?: string; imageB64?: string; loginImageB64?: string; filledImageB64?: string; title?: string; loggedIn?: boolean; loginError?: string; aborted?: boolean; error?: string }> {
  try {
    const res = await fetch(`${BASE_URL}/api/ams/browser-agent/session/open`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url,
        username: username || null,
        password: password || null,
        extra_fields: extraFields?.length ? extraFields : null,
      }),
      signal,
    });
    const d = await res.json().catch(() => ({} as any));
    if (!res.ok) return { error: d.errorMessage || `Request failed (${res.status})` };
    return {
      sessionId: d.session_id, url: d.url || url, imageB64: d.image_b64 || '',
      loginImageB64: d.login_image_b64 || '', filledImageB64: d.filled_image_b64 || '', title: d.title || '',
      loggedIn: !!d.logged_in, loginError: d.login_error || '',
    };
  } catch (e) {
    if ((e as any)?.name === 'AbortError' || signal?.aborted) return { aborted: true };
    return { error: (e as Error).message };
  }
}

/** Expand a short idea into a full browser-agent task prompt via the LLM. */
export async function composePrompt(
  idea: string,
  siteName?: string
): Promise<{ prompt?: string; error?: string }> {
  try {
    const res = await fetch(`${BASE_URL}/api/ams/browser-agent/compose-prompt`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ idea, site_name: siteName || null }),
    });
    const d = await res.json().catch(() => ({} as any));
    if (!res.ok) return { error: d.errorMessage || `Request failed (${res.status})` };
    return { prompt: d.prompt || '' };
  } catch (e) {
    return { error: (e as Error).message };
  }
}

/** Check a session is still alive and get its current screenshot (for resume). */
export async function getSessionState(
  sessionId: string
): Promise<{ alive: boolean; url?: string; imageB64?: string }> {
  try {
    const res = await fetch(`${BASE_URL}/api/ams/browser-agent/session/${encodeURIComponent(sessionId)}/state`, {
      headers: authHeaders(),
    });
    if (!res.ok) return { alive: false };
    const d = await res.json();
    return { alive: !!d.alive, url: d.url, imageB64: d.image_b64 };
  } catch {
    return { alive: false };
  }
}

/** Close a live session (best-effort). */
export async function closeSession(sessionId: string): Promise<void> {
  try {
    await fetch(`${BASE_URL}/api/ams/browser-agent/session/${encodeURIComponent(sessionId)}/close`, {
      method: 'POST',
      headers: authHeaders(),
    });
  } catch {
    /* ignore */
  }
}

/** Start a run and stream events to the provided callbacks. Resolves when the stream ends. */
export async function startRun(
  task: string,
  startingUrl: string | undefined,
  cb: RunCallbacks,
  sessionId?: string,
  extract?: ExtractConfig,
  historySeed?: AgentStep[],
  signal?: AbortSignal
): Promise<void> {
  try {
    const body: Record<string, any> = {
      task,
      starting_url: startingUrl || null,
      session_id: sessionId || null,
    };
    if (extract?.schema?.length) {
      body.schema = extract.schema;
      if (extract.maxPages) body.max_pages = extract.maxPages;
    }
    if (historySeed?.length) {
      // Carry prior steps so a continued run keeps full context instead of restarting.
      body.history_seed = historySeed.map((s) => ({
        step_num: s.step_num,
        action: s.action,
        target: s.target ?? null,
        result_text: s.result_text ?? null,
        ok: s.ok,
      }));
    }
    const response = await fetch(RUN_URL, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      let msg = `Request failed: ${response.status}`;
      try {
        const j = await response.json();
        if (j?.errorMessage) msg = j.errorMessage;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    if (!response.body) throw new Error('No response body');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const jsonStr = trimmed.slice(5).trim();
        if (!jsonStr) continue;
        try {
          const evt = JSON.parse(jsonStr);
          switch (evt.type) {
            case 'run':
              cb.onRunId?.(evt.run_id);
              break;
            case 'step':
              cb.onStep(evt as AgentStep);
              break;
            case 'screenshot':
              cb.onScreenshot(evt.step_num, evt.image_b64, evt.url || '');
              break;
            case 'input_request':
              cb.onInputRequest?.({
                stepNum: evt.step_num,
                prompt: evt.prompt,
                inputKind: evt.input_kind === 'credentials' ? 'credentials' : 'text',
                domain: evt.domain || '',
              });
              break;
            case 'extract_progress':
              cb.onExtractProgress?.(evt.count ?? 0, evt.page ?? 0);
              break;
            case 'result':
              cb.onResult(evt.status, evt.output ?? '', evt.data, evt.schema_errors);
              break;
            case 'error':
              cb.onError(evt.message ?? 'Unknown error');
              break;
            case 'done':
              cb.onDone(evt.status ?? 'unknown');
              return;
            default:
              break;
          }
        } catch {
          /* skip malformed line */
        }
      }
    }
    cb.onDone('unknown');
  } catch (err) {
    // A user-initiated stop aborts the fetch — treat it as a clean stop, not an error.
    if ((err as any)?.name === 'AbortError' || signal?.aborted) {
      cb.onDone('stopped');
      return;
    }
    cb.onError((err as Error).message);
    cb.onDone('failed');
  }
}

export async function listRuns(): Promise<RunSummary[]> {
  const res = await fetch(RUNS_URL, { headers: authHeaders() });
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data.runs) ? data.runs : [];
}

export async function getRun(runId: string): Promise<RunDetail | null> {
  const res = await fetch(`${RUNS_URL}/${encodeURIComponent(runId)}`, { headers: authHeaders() });
  if (!res.ok) return null;
  return (await res.json()) as RunDetail;
}
